import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { readUserCache, writeUserCache } from "../lib/dashboardCache";
import type { OpenClaw } from "../types/openclaw";
import type {
  OnboardingSubmission,
  SubmitErrorCode,
  SubmitResult,
} from "../types/onboarding";

const SUBMIT_URL = "/api/me/instance";
const PENDING_URL = "/api/me/instance/pending";

type ServerError = { code: string; message: string };

const isSubmitErrorCode = (code: string): code is SubmitErrorCode =>
  code === "INVALID" ||
  code === "CAP" ||
  code === "DUPLICATE" ||
  code === "UNAUTHORIZED" ||
  code === "UNKNOWN";

// The draft keeps a typed key even if the user switches provider cards, so
// nothing is lost while they explore - but the backend rejects a key on an
// openai-subscription submission (it would otherwise be pushed to SSM and
// power the tenant off a provider they opted out of). Strip it at the wire.
const pruneForAiMethod = (submission: OnboardingSubmission): OnboardingSubmission => {
  if (submission.aiSetupMethod === "openai-subscription") {
    const { openrouterApiKey: _key, ...rest } = submission;
    return rest;
  }
  return submission;
};

export const submitOnboarding = async (
  submission: OnboardingSubmission,
): Promise<SubmitResult> => {
  try {
    const res = await fetch(SUBMIT_URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getCsrfHeader() },
      body: JSON.stringify(pruneForAiMethod(submission)),
    });

    if (res.status === 202) {
      const claw = (await res.json()) as OpenClaw;
      return { ok: true, claw };
    }
    if (res.status === 401) {
      return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh and try again." };
    }
    if (res.status === 400 || res.status === 409) {
      const body = (await res.json().catch(() => null)) as ServerError | null;
      const raw = body?.code ?? "UNKNOWN";
      const code = isSubmitErrorCode(raw) ? raw : "UNKNOWN";
      return { ok: false, code, message: body?.message ?? `Submit failed (${res.status})` };
    }
    return { ok: false, code: "UNKNOWN", message: `Submit failed (${res.status})` };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, code: "UNKNOWN", message };
  }
};

export type PendingStatus = "loading" | "ready";

export type UsePendingInstanceResult = {
  status: PendingStatus;
  pending: OpenClaw | null;
  refresh: () => void;
};

// "No pending instance" is information too — cached as a wrapper object so
// a returning user with nothing pending doesn't flash a loading state.
type PendingCacheEntry = { pending: OpenClaw | null };

const PENDING_RESOURCE = "pending";

export const usePendingInstance = (): UsePendingInstanceResult => {
  const [cached] = useState<PendingCacheEntry | null>(() =>
    readUserCache<PendingCacheEntry>(PENDING_RESOURCE),
  );
  const [status, setStatus] = useState<PendingStatus>(cached !== null ? "ready" : "loading");
  const [pending, setPending] = useState<OpenClaw | null>(cached?.pending ?? null);
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    const persistEntry = (value: OpenClaw | null): void => {
      writeUserCache<PendingCacheEntry>(PENDING_RESOURCE, { pending: value });
    };
    fetch(PENDING_URL, { credentials: "include" })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 200) {
          const body = (await res.json()) as OpenClaw;
          setPending(body);
          persistEntry(body);
        } else {
          setPending(null);
          persistEntry(null);
        }
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setPending(null);
        setStatus("ready");
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = useCallback((): void => {
    setStatus("loading");
    setTick((t) => t + 1);
  }, []);

  return { status, pending, refresh };
};
